import { useEffect, useState, useCallback } from "react";
import { useAuth, useUser } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Package, Plus, Search, ArrowDownToLine, ArrowUpFromLine, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["kg", "g", "L", "ml", "pz", "confezioni", "bottiglie", "latte", "scatole"];

const emptyForm = {
  name: "", warehouse: "food", category: "Altro", unit: "kg", current_qty: 0, min_qty: 0,
  ideal_qty: 0, avg_cost: 0, supplier_name: "", expiry_date: "", shelf_location: "", notes: "",
};

export default function ProductsPage() {
  const { locationId } = useAuth();
  const user = useUser();
  const { t } = useLang();
  const [warehouse, setWarehouse] = useState("food");
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // {mode:'edit'|'new', product}
  const [movement, setMovement] = useState(null); // {product, type}
  const [form, setForm] = useState(emptyForm);
  const [movForm, setMovForm] = useState({ quantity: "", price: "", note: "" });
  const [busy, setBusy] = useState(false);

  const canDelete = ["owner", "manager"].includes(user?.role);
  const canSeeCosts = ["owner", "manager", "chef"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/products", { params: { location_id: locationId, warehouse } })
      .then((r) => setProducts(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [locationId, warehouse]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("sk:data-changed", h);
    return () => window.removeEventListener("sk:data-changed", h);
  }, [load]);

  useEffect(() => { api.get("/meta/categories").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm({ ...emptyForm, warehouse }); setDialog({ mode: "new" }); };
  const openEdit = (p) => {
    setForm({ ...emptyForm, ...p, expiry_date: p.expiry_date || "" });
    setDialog({ mode: "edit", product: p });
  };

  const save = async () => {
    setBusy(true);
    const payload = { ...form, expiry_date: form.expiry_date || null,
      current_qty: Number(form.current_qty) || 0, min_qty: Number(form.min_qty) || 0,
      ideal_qty: Number(form.ideal_qty) || 0, avg_cost: Number(form.avg_cost) || 0 };
    try {
      if (dialog.mode === "new") {
        await api.post("/products", payload, { params: { location_id: locationId } });
      } else {
        await api.put(`/products/${dialog.product.product_id}`, payload);
      }
      toast.success(t("common.success"));
      setDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const remove = async (p) => {
    if (!window.confirm(t("products.confirmDelete"))) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success(t("common.success"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitMovement = async () => {
    setBusy(true);
    try {
      await api.post("/inventory/movement", {
        product_id: movement.product.product_id, type: movement.type,
        quantity: Number(movForm.quantity), price: movForm.price ? Number(movForm.price) : null,
        note: movForm.note, location_id: locationId,
      });
      toast.success(t("common.success"));
      setMovement(null);
      setMovForm({ quantity: "", price: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
  const categories = warehouse === "food" ? meta?.food || [] : meta?.non_food || [];

  const stockBadge = (p) => {
    if (p.current_qty <= 0) return <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-red-100 text-red-800">{t("products.outOfStock")}</span>;
    if (p.min_qty > 0 && p.current_qty <= p.min_qty) return <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-amber-100 text-amber-800">{t("products.lowStock")}</span>;
    return null;
  };

  return (
    <div className="space-y-5 md:pl-14" data-testid="products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("products.title")}</h1>
        <Button data-testid="add-product-button" onClick={openNew} className="h-12 tactile-btn font-bold">
          <Plus className="h-5 w-5 mr-1" />{t("products.add")}
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="grid grid-cols-2 gap-1 p-1 bg-secondary rounded-xl" data-testid="warehouse-tabs">
          <button data-testid="warehouse-tab-food" onClick={() => setWarehouse("food")}
            className={`h-11 px-5 rounded-lg text-sm font-semibold ${warehouse === "food" ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
            {t("products.food")}
          </button>
          <button data-testid="warehouse-tab-nonfood" onClick={() => setWarehouse("non_food")}
            className={`h-11 px-5 rounded-lg text-sm font-semibold ${warehouse === "non_food" ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
            {t("products.nonFood")}
          </button>
        </div>
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="product-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t("products.search")} className="h-12 pl-9" />
        </div>
      </div>

      <div className="grid gap-2" data-testid="products-list">
        {filtered.map((p) => (
          <div key={p.product_id} data-testid={`product-row-${p.product_id}`}
            className="bg-card border rounded-2xl p-3 sm:p-4 flex items-center gap-3 flex-wrap">
            <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center shrink-0">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-36">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-sm">{p.name}</p>
                {stockBadge(p)}
              </div>
              <p className="text-xs text-muted-foreground">
                {p.category}{p.supplier_name ? ` · ${p.supplier_name}` : ""}
                {canSeeCosts && p.avg_cost ? ` · €${p.avg_cost}/${p.unit}` : ""}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono font-bold text-lg leading-tight">{p.current_qty} <span className="text-xs font-medium text-muted-foreground">{p.unit}</span></p>
              {p.min_qty > 0 && <p className="text-[10px] text-muted-foreground">min {p.min_qty} {p.unit}</p>}
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button data-testid={`load-${p.product_id}`} title={t("products.load")}
                onClick={() => { setMovement({ product: p, type: "load" }); setMovForm({ quantity: "", price: "", note: "" }); }}
                className="tactile-btn h-11 w-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
                <ArrowDownToLine className="h-5 w-5" />
              </button>
              <button data-testid={`unload-${p.product_id}`} title={t("products.unload")}
                onClick={() => { setMovement({ product: p, type: "unload" }); setMovForm({ quantity: "", price: "", note: "" }); }}
                className="tactile-btn h-11 w-11 rounded-xl bg-sky-600 text-white flex items-center justify-center">
                <ArrowUpFromLine className="h-5 w-5" />
              </button>
              <button data-testid={`edit-${p.product_id}`} onClick={() => openEdit(p)}
                className="tactile-btn h-11 w-11 rounded-xl border flex items-center justify-center text-muted-foreground">
                <Pencil className="h-4 w-4" />
              </button>
              {canDelete && (
                <button data-testid={`delete-${p.product_id}`} onClick={() => remove(p)}
                  className="tactile-btn h-11 w-11 rounded-xl border flex items-center justify-center text-muted-foreground hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        {!filtered.length && (
          <div className="text-center py-16 text-muted-foreground text-sm" data-testid="products-empty">
            <Package className="h-10 w-10 mx-auto mb-2 opacity-40" />
            {t("products.search")}
          </div>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{dialog?.mode === "new" ? t("products.add") : form.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label>{t("products.name")}</Label>
              <Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("products.title")}</Label>
              <select data-testid="product-form-warehouse" value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm">
                <option value="food">{t("products.food")}</option>
                <option value="non_food">{t("products.nonFood")}</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("products.category")}</Label>
              <select data-testid="product-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm">
                {(form.warehouse === "food" ? meta?.food || [] : meta?.non_food || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("products.unit")}</Label>
              <select data-testid="product-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm">
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("products.qty")}</Label>
              <Input data-testid="product-form-qty" type="number" step="0.01" value={form.current_qty} onChange={(e) => setForm({ ...form, current_qty: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("products.minQty")}</Label>
              <Input data-testid="product-form-min" type="number" step="0.01" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("products.idealQty")}</Label>
              <Input data-testid="product-form-ideal" type="number" step="0.01" value={form.ideal_qty} onChange={(e) => setForm({ ...form, ideal_qty: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("products.cost")} (€/{form.unit})</Label>
              <Input data-testid="product-form-cost" type="number" step="0.01" value={form.avg_cost} onChange={(e) => setForm({ ...form, avg_cost: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("products.supplier")}</Label>
              <Input data-testid="product-form-supplier" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("products.expiry")}</Label>
              <Input data-testid="product-form-expiry" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("products.location")}</Label>
              <Input data-testid="product-form-location" value={form.shelf_location} onChange={(e) => setForm({ ...form, shelf_location: e.target.value })} className="h-11" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="product-form-save" onClick={save} disabled={busy || !form.name} className="flex-1 h-12 tactile-btn font-bold">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("common.save")}
            </Button>
            <Button data-testid="product-form-cancel" variant="outline" onClick={() => setDialog(null)} className="h-12">{t("common.cancel")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!movement} onOpenChange={() => setMovement(null)}>
        <DialogContent className="max-w-sm" data-testid="movement-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {movement?.type === "load" ? t("products.load") : t("products.unload")} — {movement?.product.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t("products.quantity")} ({movement?.product.unit})</Label>
              <Input data-testid="movement-quantity" type="number" step="0.01" min="0" autoFocus
                value={movForm.quantity} onChange={(e) => setMovForm({ ...movForm, quantity: e.target.value })}
                className="h-14 text-xl font-mono text-center" />
            </div>
            {movement?.type === "load" && (
              <div className="space-y-1">
                <Label>{t("products.price")} (€/{movement?.product.unit})</Label>
                <Input data-testid="movement-price" type="number" step="0.01"
                  value={movForm.price} onChange={(e) => setMovForm({ ...movForm, price: e.target.value })} className="h-11" />
              </div>
            )}
            <div className="space-y-1">
              <Label>{t("products.note")}</Label>
              <Input data-testid="movement-note" value={movForm.note} onChange={(e) => setMovForm({ ...movForm, note: e.target.value })} className="h-11" />
            </div>
            <Button data-testid="movement-submit" onClick={submitMovement}
              disabled={busy || !movForm.quantity || Number(movForm.quantity) <= 0}
              className={`w-full h-12 tactile-btn font-bold ${movement?.type === "load" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-sky-600 hover:bg-sky-700"}`}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("dash.confirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
